import Star from '@amo/core/assets/images/icons/star.png';
import { normalizeEuro } from '@amo/core/utils/normalize';
import { getTranslation } from '@amo/core/utils/index.js';
import _ from 'lodash';
import getPriceByFractionnementOrCodePromo from '../utilsTarif/getPriceByFractionnementOrCodePromo';

const FormuleGrid = ({ formules, formuleChoisie, formulePreconisee, fractionnementChoisi, codePromo, changeValue, analyticsSetEvent }) => {
    const lastFormula = _.last(formules);
    const inclusionGuarantees = lastFormula?.ListeGaranties?.filter(g => g.Disponible === 'Inclusion') || [];

    const convertToFloat = (price) => parseFloat(price?.replace(/[^\d,.-]/g, '').replace(',', '.') || '0');

    const renderPrice = (priceData) => {
        const prixVal = convertToFloat(priceData.prix);
        const prixPromoVal = convertToFloat(priceData.prixPromo);
        const hasDiscount = prixVal > prixPromoVal;
        const isPromo = codePromo?.Eligible && hasDiscount;
        return (
            <>
                <div className="formule-card-op__price">
                    {isPromo ? priceData.prixPromo : priceData.prix}
                </div>
                {isPromo && (
                    <div className="formule-card-op__price-old">{priceData.prix}</div>
                )}
            </>
        );
    };

    const handleSelect = (f) => {
        analyticsSetEvent({
            event: 'changementFormule',
            datas: { changementFormule: { formule: f.LibelleFormule.toUpperCase() } },
        });
        changeValue('DemandeTarif[Police][FormuleChoisie]', f.CodeFormule);
        changeValue('options', {});
        changeValue('optionsFranchise', { RAFRAVI: 'false', RAFRADTA: 'false' });
        changeValue('optionsListe', {});
    };

    const renderStars = (f) => {
        const gold = f.CodeFormule === 'F1' ? 1 : f.CodeFormule === 'F2' ? 2 : 3;
        return (
            <div className="formule-card-op__stars">
                {_.times(3, (i) => (
                    <img
                        key={i}
                        src={Star}
                        alt=""
                        width={20}
                        loading="lazy"
                        aria-hidden="true"
                        style={i >= gold ? { filter: 'grayscale(1) opacity(0.85)' } : undefined}
                    />
                ))}
            </div>
        );
    };

    const renderGuarantees = (f) => (
        <ul className="formule-card-op__features">
            {inclusionGuarantees.map((g, idx) => {
                const isIncluded = _.find(f.ListeGaranties, (h) =>
                    h.CodeGarantie === g.CodeGarantie && h.Disponible === 'Inclusion'
                );
                return (
                    <li key={idx}>
                        <span className={`fi-icon ${isIncluded ? 'fi-ok' : 'fi-no'}`}>
                            {isIncluded ? '\u2714' : '\u2716'}
                        </span>
                        <span>{g.LibelleGarantie}</span>
                    </li>
                );
            })}
        </ul>
    );

    const selectedFormula = formuleChoisie || formules[0];
    const selectedPriceData = selectedFormula ? getPriceByFractionnementOrCodePromo(selectedFormula, fractionnementChoisi) : null;

    return (
        <>
            {/* Desktop/Tablette : grille 3 colonnes */}
            <div className="formules-grid--desktop" role="radiogroup" aria-label="Choix de la formule">
                {formules.map((f) => {
                    const isSelected = f.CodeFormule === formuleChoisie?.CodeFormule;
                    const isRecommended = formulePreconisee === f.CodeFormule;
                    const priceData = getPriceByFractionnementOrCodePromo(f, fractionnementChoisi);

                    return (
                        <div
                            key={f.CodeFormule}
                            className={`formule-card-op ${isSelected ? 'active' : ''}`}
                            onClick={() => !isSelected && handleSelect(f)}
                            role="radio"
                            aria-checked={isSelected}
                            aria-label={`Formule ${f.LibelleFormule}`}
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && !isSelected && handleSelect(f)}
                            style={isSelected ? {cursor: 'default'} : undefined}
                        >
                            {isRecommended && (
                                <div className="formule-card-op__ribbon"><span>Recommandée</span></div>
                            )}
                            <div className="formule-card-op__name">{f.LibelleFormule}</div>
                            {renderPrice(priceData)}
                            <div className="formule-card-op__period">
                                TTC / {fractionnementChoisi === 'M' ? getTranslation('months') : getTranslation('year')}
                                {fractionnementChoisi === 'M' && (
                                    <span className="formule-card-op__annual">
                                        {' '}soit {normalizeEuro(Math.round(f.PrimeMensuelleBase * 12 * 100) / 100)} TTC/{getTranslation('year')}
                                    </span>
                                )}
                            </div>
                            {renderStars(f)}
                            {renderGuarantees(f)}
                        </div>
                    );
                })}
            </div>

            {/* Mobile : tabs + détail */}
            <div className="formules-mobile">
                <div className="formule-tabs" role="tablist" aria-label="Choix de la formule">
                    {formules.map((f) => {
                        const isSelected = f.CodeFormule === formuleChoisie?.CodeFormule;
                        const isRecommended = formulePreconisee === f.CodeFormule;
                        return (
                            <button
                                key={f.CodeFormule}
                                className={`formule-tab ${isSelected ? 'active' : ''}`}
                                onClick={() => !isSelected && handleSelect(f)}
                                style={isSelected ? {cursor: 'default'} : undefined}
                                role="tab"
                                aria-selected={isSelected}
                                aria-controls={`formule-panel-${f.CodeFormule}`}
                            >
                                {f.LibelleFormule}
                                {isRecommended && <span className="formule-tab__reco">Recommandée</span>}
                            </button>
                        );
                    })}
                </div>
                {selectedFormula && (
                    <div className="formule-detail" role="tabpanel" id={`formule-panel-${selectedFormula.CodeFormule}`} aria-label={`Détail formule ${selectedFormula.LibelleFormule}`}>
                        <div className="formule-detail__header">
                            <div>
                                <div className="formule-card-op__name">{selectedFormula.LibelleFormule}</div>
                                {renderStars(selectedFormula)}
                            </div>
                            <div className="formule-detail__price-block">
                                {selectedPriceData && renderPrice(selectedPriceData)}
                                <div className="formule-card-op__period">
                                    TTC / {fractionnementChoisi === 'M' ? getTranslation('months') : getTranslation('year')}
                                    {fractionnementChoisi === 'M' && (
                                        <span className="formule-card-op__annual">
                                            {' '}soit {normalizeEuro(Math.round(selectedFormula.PrimeMensuelleBase * 12 * 100) / 100)} TTC/{getTranslation('year')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        {renderGuarantees(selectedFormula)}
                    </div>
                )}
            </div>
        </>
    );
};

export default FormuleGrid;
